import CustomerOrdersApiV2 from 'api/v2/admins/customer-orders-api-v2'
import CustomerShipmentsApiV2 from 'api/v2/admins/customer-shipments-api-v2'
import LoaderButton from 'components/molecules/loader-button'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import CustomerOrderCourierSelectInput from 'domains/customer-order/atoms/courier-select-input/customer-order-courier-select-input'
import CustomerOrderCreateShipmentTable from 'domains/customer-order/organisms/create-shipment-table'
import CustomerOrderShippingAddressCard from 'domains/customer-order/organisms/shipping-address-card'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import {
  Card,
  CardBody, CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  CustomInput,
  Alert
} from 'reactstrap'

import { Link, useHistory } from 'react-router-dom'
import SHIPMENT_METHOD from 'domains/customer-checkout/constants/checkout-shipment-method'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

/**
 * warning alert on modal
 * @param {boolean} isOpen
 * @param {object} shippingAddress
 */
/* eslint-disable react/prop-types */
const WarningModal = ({ isOpen, shippingAddress }) => (
  <Modal isOpen={ isOpen } zIndex={ 800 } centered={ true } className="text-center">
    { /* remove toogle to prevent user close the modal */ }
    <ModalHeader className="bg-danger justify-content-center">
      <label className="text-white">
        { 'WARNING' }
      </label>
    </ModalHeader>
    <ModalBody>
      <div className='py-3'>
        { 'Alamat User tidak lengkap. Mohon update dan lengkapi alamat user sebelum membuat shipment baru!' }
      </div>
      <Button
        color='warning'
        tag={ Link }
        to={ `/app/user-address/${shippingAddress?.id}/edit` }
      >
        { 'Update Address' }
      </Button>
    </ModalBody>
  </Modal>
)

const CustomerOrderNewShipmentPage = ({ pageUtils }) => {
  const { id }                                = useParams()
  const [ order, setOrder ]                   = useState({})
  const [ isOrderLoading, setIsOrderLoading ] = useState(true)
  const [ isSubmitting, setIsSubmitting ]     = useState(false)
  // Order Items going into the new shipment
  const [ chosenOrderItems, setChosenOrderItems ] = useState([])


  // Shipment Methods to display
  const [ isShipmentMethodLoading, setIsShipmentMethodLoading ] = useState(false)
  const [ shipmentMethods, setShipmentMethods ]                 = useState([])
  const [ chosenShipmentMethod, setChosenShipmentMethod ]       = useState(null)

  const [ isInsurance, setIsInsurance ] = useState(false)
  const history                         = useHistory()

  const totalPrice           = order?.item_price_total
  const isMandatoryInsurance = totalPrice > 1000000
  const isInsuranceAvailable = chosenShipmentMethod?.insurance_cost > 0
  const isCheckboxDisabled   = chosenShipmentMethod && !isInsuranceAvailable

  const isGrabShipment     = chosenShipmentMethod?.courier == SHIPMENT_METHOD.GRAB_EXPRESS
  const showInsuranceAlert = !isInsuranceAvailable && !isGrabShipment

  const orderId = parseInt(id)

  useEffect(() => {
    CustomerOrdersApiV2.show(id)
      .then((response) => {
        setOrder(response.data.order  )
        setIsOrderLoading(false)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ id, pageUtils ])


  /**
   * Validate shipping_address
   * - some addressed migrated from Magento may not have updated data
   */
  const [ isAddressNotComplete, setIsAddressNotComplete ] = useState(false)
  useEffect(() => {
    // check if the address is correct (all filled)
    if (order.shipping_address) {
      setIsAddressNotComplete(
        !order.shipping_address.tl_province ||
        !order.shipping_address.tl_city ||
        !order.shipping_address.tl_district ||
        !order.shipping_address.tl_sub_district
      )
    }
  }, [ order ])

  /**
   * Clicking on `Add` Button in the Order Items Table
   * - update `shipment.items` state
   * - update the selected `orderItemIds`
   * - reset courier
   *
   * @param {object} selectedOrderItem - object that represents backend order_item
   */
  const handleAddItemClick = (selectedOrderItem) => {
    setChosenOrderItems((prevState) => (
      [ ...prevState, selectedOrderItem ]
    ))
  }

  /**
   * Click on `Remove` Button in the Shipment Items Table
   *
   * @param {object} shipmentItem
   */
  const handleRemoveItemClick = (selectedOrderItem) => {
    setChosenOrderItems((prevState) => (
      prevState.filter((item) => item.id !== selectedOrderItem.id)
    ))
  }

  /**
   * Get cost for Shipment with selected order_items going into the shipment
   */
  const handleCourierClick = () => {
    setIsShipmentMethodLoading(true)
    const orderAffiliateName = order.affiliate_name
    const isZaloraOrder      = orderAffiliateName === 'zalora'
    const params             = {
      orderId            : orderId,
      selectedOrderItems : chosenOrderItems,
      courier            : isZaloraOrder && 'JNE'
    }
    CustomerShipmentsApiV2.shipmentMethods(params)
      .then((response) => {
        setShipmentMethods(response.data)
        setChosenShipmentMethod(null)
        setIsShipmentMethodLoading(false)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        setIsShipmentMethodLoading(false)
      })
  }

  /**
   *
   * @param {shipMethod} shipMethod - object from fetching couriers and their costs
   */
  const handleShipmentMethodChange = (shipMethod) => {
    setChosenShipmentMethod(shipMethod)
    if (shipMethod?.insurance_cost > 0) {
      setIsInsurance(true)
    } else {
      setIsInsurance(false)
    }
  }

  /**
   * Transform state data into a shipment object for backend
   */
  const handleCreateButtonClick = async () => {
    const updatedShipmentMethod = {
      ...chosenShipmentMethod,
      insurance_cost: isInsurance ? chosenShipmentMethod.insurance_cost : 0
    }
    setIsSubmitting(true)
    await CustomerShipmentsApiV2.create(order, chosenOrderItems, updatedShipmentMethod)
      .then(() => {
        pageUtils.setAlertMsg('Shipment has been Created', ALERT_TYPES.SUCCESS)
        setTimeout(() => history.goBack(), 1000)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsSubmitting(false))
  }

  /**
   * Reset Courier whenever chosenOrderItems change
   */
  useEffect(() => {
    setShipmentMethods([])
    setChosenShipmentMethod(null)
  }, [ chosenOrderItems ])

  const courierButtonDisabled = chosenOrderItems.length === 0
  const createButtonDisabled  = chosenOrderItems.length === 0 || !chosenShipmentMethod

  return (
    <>
      <WarningModal
        isOpen={ isAddressNotComplete }
        shippingAddress={ order.shipping_address }
      />
      <TitlePage
        mainTitle={ 'Create New Shipment for Order' }
        subTitle={ `# ${order.number}` }
      />
      <Row>

        { /* Left Column  */ }
        <Col md={ 9 }>
          <Card>
            <CardHeader tag={ 'h3' }>
              { 'Order Items' }
            </CardHeader>
            <CustomerOrderCreateShipmentTable
              data={ order.order_items }
              onClickItem={ handleAddItemClick }
              chosenOrderItems = { chosenOrderItems }
            />
          </Card>

          <Card>
            <CardHeader tag={ 'h3' }>
              { 'Shipment Items' }
            </CardHeader>
            <CustomerOrderCreateShipmentTable
              data={ chosenOrderItems }
              onClickItem={ handleRemoveItemClick }
              isShipmentItems={ true }
            />
          </Card>

          <Card>
            <CardHeader tag={ 'h3' }className={ 'd-flex justify-content-between align-items-center' }>
              { 'Shipment Methods' }
              <LoaderButton
                color='primary'
                onClick={ handleCourierClick }
                text='Select Courier'
                disabled={ courierButtonDisabled }
                isLoading={ isShipmentMethodLoading }
              />
            </CardHeader>
            <CardBody>
              {
                shipmentMethods.length > 0 &&
                 (
                   <CustomerOrderCourierSelectInput
                     shipmentMethods={ shipmentMethods }
                     chosenShipmentMethod={ chosenShipmentMethod }
                     onClickItem={ handleShipmentMethodChange }
                   />
                 )
              }
              {
                chosenShipmentMethod && isMandatoryInsurance && !isGrabShipment && (
                  <Alert color='info' className='small'>{ 'Asuransi Pengiriman wajib untuk order diatas Rp. 1,000,000' }</Alert>
                )
              }
              {
                chosenShipmentMethod && showInsuranceAlert && (
                  <Alert color='info' className='small'>{ `untuk shipment method ${chosenShipmentMethod?.courier}-${chosenShipmentMethod?.service_type} Order ini tidak bisa dicover oleh asuransi karena total harga yang rendah.` }</Alert>
                )
              }

              {
                chosenShipmentMethod && !isGrabShipment && (
                  <CustomInput
                    id='customer-shipment-checkbox'
                    type='checkbox'
                    disabled={ isCheckboxDisabled }
                    checked={ isInsurance || isMandatoryInsurance }
                    onClick={ () => setIsInsurance(!isInsurance) }
                    label={ 'Shipping Insurance' }
                  />
                )
              }

            </CardBody>
          </Card>

          <Button
            className='float-right'
            color='success'
            size={ 'lg' }
            onClick={ handleCreateButtonClick }
            disabled={ createButtonDisabled }
            isLoading={ isSubmitting }
          >
            { 'CREATE SHIPMENT' }
          </Button>

        </Col>

        { /* Right Col - Data */ }
        <Col md={ 3 }>
          {
            isAddressNotComplete && (
              <Alert color='warning'>
                <i className='fas fa-info-circle mr-2' />
                { 'Mohon update dan lengkapi alaman user terlebih dahulu.' }
              </Alert>
            )
          }
          <CustomerOrderShippingAddressCard
            isLoading={ isOrderLoading }
            shippingAddress={ order.shipping_address || {} }
          />
        </Col>

      </Row>

    </>
  )
}

CustomerOrderNewShipmentPage.propTypes = propTypes

export default CustomerOrderNewShipmentPage
